import axios, { AxiosResponse } from 'axios';
import useAuthStore from '../store/authStore';
import type {
  LoginResponse,
  User,
  Plant,
  LiveReadingsMap,
  SensorReading,
  AggregatedReading,
  DailyGenerationSummary,
  Alarm,
  AlarmStats,
  AlarmType,
  AnomalyType,
  LoggedAnomaly,
  ScadaTag,
  ScadaSettings,
  ScadaSettingsForm,
  NotificationSetting,
  PollerStatus,
  CreateUserForm,
  ManualEntryForm,
  PlantWithUsers,
  UserWithPlant,
  Role,
  PaginatedResponse,
  PlantHealthSummary,
  EarlyWarning,
  MaintenanceRecommendation,
  HydrologyForecast,
  DayForecast,
  Generator,
  Transformer,
  GenerationEstimate,
  MaintenanceLog,
} from '../types';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({ baseURL: API_URL, timeout: 15_000 });

api.interceptors.request.use((config) => {
  const token = useAuthStore.getState().token;
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (r) => r,
  (err: unknown) => {
    const e = err as { config?: { url?: string }; response?: { status?: number } };
    if (e.response?.status === 401 && !e.config?.url?.includes('/auth/login')) {
      useAuthStore.getState().logout();
      window.location.href = '/login';
    }
    return Promise.reject(err);
  }
);

// ── Auth ─────────────────────────────────────────────────────────────────────
export const authApi = {
  login: (email: string, password: string): Promise<AxiosResponse<LoginResponse>> =>
    api.post<LoginResponse>('/auth/login', { email, password }),

  me: (): Promise<AxiosResponse<{ user: User }>> =>
    api.get<{ user: User }>('/auth/me'),

  changePassword: (data: { current_password: string; new_password: string }): Promise<AxiosResponse<void>> =>
    api.post('/auth/change-password', data),

  listRoles: (): Promise<AxiosResponse<{ roles: { id: number; name: string }[] }>> =>
    api.get('/auth/roles'),

  listUsers: (): Promise<AxiosResponse<{ users: User[] }>> =>
    api.get<{ users: User[] }>('/auth/users'),

  createUser: (data: CreateUserForm): Promise<AxiosResponse<{ user: User }>> =>
    api.post<{ user: User }>('/auth/users', data),

  updateUser: (id: string, data: Partial<User> & { password?: string }): Promise<AxiosResponse<{ user: User }>> =>
    api.patch<{ user: User }>(`/auth/users/${id}`, data),
};

// ── Readings ─────────────────────────────────────────────────────────────────
interface HistoryParams {
  tagName:  string;
  from:     string;
  to:       string;
  limit?:   number;
  offset?:  number;
}

interface AggregatedParams {
  tagName: string;
  from:    string;
  to:      string;
  bucket:  string;
}

interface SummariesParams {
  from: string;
  to:   string;
}

export const readingsApi = {
  live: (plantId = 1): Promise<AxiosResponse<{ data: LiveReadingsMap }>> =>
    api.get<{ data: LiveReadingsMap }>('/readings/live', { params: { plantId } }),

  history: (params: HistoryParams): Promise<AxiosResponse<{ data: SensorReading[] }>> =>
    api.get<{ data: SensorReading[] }>('/readings/history', { params }),

  aggregated: (params: AggregatedParams): Promise<AxiosResponse<{ data: AggregatedReading[] }>> =>
    api.get<{ data: AggregatedReading[] }>('/readings/aggregated', { params }),

  summaries: (params: SummariesParams): Promise<AxiosResponse<{ data: DailyGenerationSummary[] }>> =>
    api.get<{ data: DailyGenerationSummary[] }>('/readings/summaries', { params }),

  manualEntry: (data: ManualEntryForm): Promise<AxiosResponse<{ data: DailyGenerationSummary }>> =>
    api.post<{ data: DailyGenerationSummary }>('/readings/manual', data),

  uploadExcel: (file: File, plantId?: number): Promise<AxiosResponse<{
    imported:      number;
    skipped_count: number;
    skipped:       string[];
    rows:          DailyGenerationSummary[];
  }>> => {
    const fd = new FormData();
    fd.append('file', file);
    if (plantId) fd.append('plant_id', String(plantId));
    return api.post('/readings/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
};

// ── Alarms ───────────────────────────────────────────────────────────────────
interface AlarmListParams {
  status?:   string;
  severity?: string;
  limit?:    number;
  offset?:   number;
}

export const alarmsApi = {
  list: (params?: AlarmListParams): Promise<AxiosResponse<PaginatedResponse<Alarm>>> =>
    api.get<PaginatedResponse<Alarm>>('/alarms', { params }),

  stats: (): Promise<AxiosResponse<AlarmStats>> =>
    api.get<AlarmStats>('/alarms/stats'),

  listTypes: (): Promise<AxiosResponse<{ alarm_types: AlarmType[] }>> =>
    api.get<{ alarm_types: AlarmType[] }>('/alarms/types'),

  create: (data: {
    alarm_type_id: number;
    severity?:     string;
    message?:      string;
    reported_at?:  string;
    notes?:        string;
    plant_id?:     number;
  }): Promise<AxiosResponse<{ alarm: Alarm }>> =>
    api.post<{ alarm: Alarm }>('/alarms', data),

  acknowledge: (id: number, notes?: string): Promise<AxiosResponse<{ alarm: Alarm }>> =>
    api.patch<{ alarm: Alarm }>(`/alarms/${id}/acknowledge`, { notes }),

  addNote: (id: number, notes: string): Promise<AxiosResponse<void>> =>
    api.patch(`/alarms/${id}/notes`, { notes }),
};

// ── Reports ──────────────────────────────────────────────────────────────────
interface DailyReportParams {
  date:    string;
  format?: string;
}

interface MonthlyReportParams {
  year:   number;
  month:  number;
}

export const reportsApi = {
  daily: (params: DailyReportParams): Promise<AxiosResponse<{ data: DailyGenerationSummary }>> =>
    api.get<{ data: DailyGenerationSummary }>('/reports/daily', { params }),

  monthly: (params: MonthlyReportParams): Promise<AxiosResponse<{ data: DailyGenerationSummary[] }>> =>
    api.get<{ data: DailyGenerationSummary[] }>('/reports/monthly', { params }),

  weekly: (weekOffset: number): Promise<AxiosResponse<{ data: DailyGenerationSummary[] }>> =>
    api.get<{ data: DailyGenerationSummary[] }>('/reports/weekly', { params: { weekOffset } }),

  downloadDaily: (date: string, format: string): Promise<AxiosResponse<Blob>> =>
    api.get<Blob>('/reports/daily', { params: { date, format }, responseType: 'blob' }),

  downloadMonthly: (year: number, month: number, format: string): Promise<AxiosResponse<Blob>> =>
    api.get<Blob>('/reports/monthly', { params: { year, month, format }, responseType: 'blob' }),

  downloadWeekly: (weekOffset: number, format: string): Promise<AxiosResponse<Blob>> =>
    api.get<Blob>('/reports/weekly', { params: { weekOffset, format }, responseType: 'blob' }),

  erc: (type: 'daily' | 'weekly' | 'monthly', opts: Record<string, string | number | undefined>): Promise<AxiosResponse<{ data: unknown }>> =>
    api.get('/reports/erc', { params: { type, ...opts } }),

  downloadErc: (type: string, opts: Record<string, string | number | undefined>, fmt: string): Promise<AxiosResponse<Blob>> =>
    api.get<Blob>('/reports/erc', { params: { type, format: fmt, ...opts }, responseType: 'blob' }),

  invoice: (year: number, month: number, rate: number): Promise<AxiosResponse<{ data: unknown }>> =>
    api.get('/reports/invoice', { params: { year, month, rate } }),

  downloadInvoice: (year: number, month: number, rate: number, fmt: string): Promise<AxiosResponse<Blob>> =>
    api.get<Blob>('/reports/invoice', { params: { year, month, rate, format: fmt }, responseType: 'blob' }),
};

// ── Anomaly ──────────────────────────────────────────────────────────────────
export const anomalyApi = {
  health: (plantId = 1, refresh = false): Promise<AxiosResponse<{ data: PlantHealthSummary }>> =>
    api.get<{ data: PlantHealthSummary }>('/anomaly/health', { params: { plantId, refresh } }),

  warnings: (plantId = 1): Promise<AxiosResponse<{ data: EarlyWarning[]; count: number }>> =>
    api.get<{ data: EarlyWarning[]; count: number }>('/anomaly/warnings', { params: { plantId } }),

  listTypes: (): Promise<AxiosResponse<{ anomaly_types: AnomalyType[] }>> =>
    api.get<{ anomaly_types: AnomalyType[] }>('/anomaly/types'),

  listLogged: (params?: { status?: string; severity?: string; limit?: number; offset?: number }): Promise<AxiosResponse<{ data: LoggedAnomaly[]; total: number }>> =>
    api.get('/anomaly/logged', { params }),

  createLogged: (data: {
    anomaly_type_id: number;
    severity?:       string;
    message?:        string;
    reported_at?:    string;
    notes?:          string;
    plant_id?:       number;
  }): Promise<AxiosResponse<{ anomaly: LoggedAnomaly }>> =>
    api.post('/anomaly/logged', data),

  acknowledgeLogged: (id: number, notes?: string): Promise<AxiosResponse<{ anomaly: LoggedAnomaly }>> =>
    api.patch(`/anomaly/logged/${id}/acknowledge`, { notes }),
};

// ── Maintenance ───────────────────────────────────────────────────────────────
export const maintenanceApi = {
  recommendations: (): Promise<AxiosResponse<{ data: MaintenanceRecommendation[] }>> =>
    api.get<{ data: MaintenanceRecommendation[] }>('/reports/maintenance'),
};

// ── Admin ────────────────────────────────────────────────────────────────────
export const adminApi = {
  scadaSettings: (): Promise<AxiosResponse<{ settings: ScadaSettings }>> =>
    api.get<{ settings: ScadaSettings }>('/admin/scada-settings'),

  updateScada: (data: ScadaSettingsForm): Promise<AxiosResponse<{ settings: ScadaSettings }>> =>
    api.patch<{ settings: ScadaSettings }>('/admin/scada-settings', data),

  tags: (): Promise<AxiosResponse<{ tags: ScadaTag[] }>> =>
    api.get<{ tags: ScadaTag[] }>('/admin/tags'),

  updateTag: (id: number, data: Partial<ScadaTag>): Promise<AxiosResponse<{ tag: ScadaTag }>> =>
    api.patch<{ tag: ScadaTag }>(`/admin/tags/${id}`, data),

  createTag: (data: Partial<ScadaTag>): Promise<AxiosResponse<{ tag: ScadaTag }>> =>
    api.post<{ tag: ScadaTag }>('/admin/tags', data),

  pollerStatus: (): Promise<AxiosResponse<PollerStatus>> =>
    api.get<PollerStatus>('/admin/poller/status'),

  notifications: (): Promise<AxiosResponse<{ settings: NotificationSetting[] }>> =>
    api.get<{ settings: NotificationSetting[] }>('/admin/notifications'),

  updateNotification: (id: number, data: Partial<NotificationSetting>): Promise<AxiosResponse<void>> =>
    api.patch(`/admin/notifications/${id}`, data),

  auditLogs: (): Promise<AxiosResponse<{ logs: unknown[] }>> =>
    api.get('/admin/audit-logs'),

  triggerDailySummary: (date?: string): Promise<AxiosResponse<void>> =>
    api.post('/admin/daily-summary', { date }),

  maintenanceLogs: (): Promise<AxiosResponse<{ logs: MaintenanceLog[] }>> =>
    api.get('/admin/maintenance-logs'),

  createMaintenanceLog: (data: {
    equipment_type: 'generator' | 'transformer';
    equipment_id:   number;
    maintenance_type?: string;
    description?:    string;
    performed_by?:   string;
    started_at?:     string;
    completed_at?:   string;
    downtime_minutes?: number;
    cost?:           number;
  }): Promise<AxiosResponse<{ log: MaintenanceLog }>> =>
    api.post('/admin/maintenance-logs', data),

  deleteMaintenanceLog: (id: number): Promise<AxiosResponse<{ ok: boolean }>> =>
    api.delete(`/admin/maintenance-logs/${id}`),

  plant: (): Promise<AxiosResponse<{ plant: Plant | null }>> =>
    api.get('/admin/plant'),

  generators: (): Promise<AxiosResponse<{ generators: Generator[] }>> =>
    api.get('/admin/generators'),

  transformers: (): Promise<AxiosResponse<{ transformers: Transformer[] }>> =>
    api.get('/admin/transformers'),
};

// ── Hydrology ─────────────────────────────────────────────────────────────────
export const hydrologyApi = {
  forecast: (refresh = false): Promise<AxiosResponse<{ data: HydrologyForecast }>> =>
    api.get<{ data: HydrologyForecast }>('/hydrology/forecast', { params: { refresh } }),

  dispatch: (day = 0): Promise<AxiosResponse<{ data: DayForecast }>> =>
    api.get<{ data: DayForecast }>('/hydrology/dispatch', { params: { day } }),
};

// ── Super Admin ───────────────────────────────────────────────────────────────
export const superAdminApi = {
  listPlants: (): Promise<AxiosResponse<{ plants: PlantWithUsers[] }>> =>
    api.get('/super-admin/plants'),

  createPlant: (data: Partial<Plant>): Promise<AxiosResponse<{ plant: Plant }>> =>
    api.post('/super-admin/plants', data),

  updatePlant: (id: number, data: Partial<Plant>): Promise<AxiosResponse<{ plant: Plant }>> =>
    api.patch(`/super-admin/plants/${id}`, data),

  listGenerators: (plantId: number): Promise<AxiosResponse<{ generators: Generator[] }>> =>
    api.get(`/super-admin/plants/${plantId}/generators`),

  createGenerator: (plantId: number, data: Partial<Generator>): Promise<AxiosResponse<{ generator: Generator }>> =>
    api.post(`/super-admin/plants/${plantId}/generators`, data),

  updateGenerator: (plantId: number, id: number, data: Partial<Generator>): Promise<AxiosResponse<{ generator: Generator }>> =>
    api.patch(`/super-admin/plants/${plantId}/generators/${id}`, data),

  getTransformer: (plantId: number): Promise<AxiosResponse<{ transformer: Transformer | null }>> =>
    api.get(`/super-admin/plants/${plantId}/transformer`),

  upsertTransformer: (plantId: number, data: Partial<Transformer>): Promise<AxiosResponse<{ transformer: Transformer }>> =>
    api.put(`/super-admin/plants/${plantId}/transformer`, data),

  listUsers: (): Promise<AxiosResponse<{ users: UserWithPlant[] }>> =>
    api.get('/super-admin/users'),

  createUser: (data: { name: string; email: string; password: string; role_id: number; plant_id: number | null }): Promise<AxiosResponse<{ user: User }>> =>
    api.post('/super-admin/users', data),

  updateUser: (id: string, data: { name?: string; role_id?: number; plant_id?: number | null; is_active?: boolean; password?: string }): Promise<AxiosResponse<{ user: User }>> =>
    api.patch(`/super-admin/users/${id}`, data),

  listRoles: (): Promise<AxiosResponse<{ roles: Role[] }>> =>
    api.get('/super-admin/roles'),

  // Alarm type categories
  listAlarmTypes: (): Promise<AxiosResponse<{ alarm_types: AlarmType[] }>> =>
    api.get('/super-admin/alarm-types'),
  createAlarmType: (data: Partial<AlarmType>): Promise<AxiosResponse<{ alarm_type: AlarmType }>> =>
    api.post('/super-admin/alarm-types', data),
  updateAlarmType: (id: number, data: Partial<AlarmType>): Promise<AxiosResponse<{ alarm_type: AlarmType }>> =>
    api.patch(`/super-admin/alarm-types/${id}`, data),
  deleteAlarmType: (id: number): Promise<AxiosResponse<{ deleted: number }>> =>
    api.delete(`/super-admin/alarm-types/${id}`),

  // Anomaly type categories
  listAnomalyTypes: (): Promise<AxiosResponse<{ anomaly_types: AnomalyType[] }>> =>
    api.get('/super-admin/anomaly-types'),
  createAnomalyType: (data: Partial<AnomalyType>): Promise<AxiosResponse<{ anomaly_type: AnomalyType }>> =>
    api.post('/super-admin/anomaly-types', data),
  updateAnomalyType: (id: number, data: Partial<AnomalyType>): Promise<AxiosResponse<{ anomaly_type: AnomalyType }>> =>
    api.patch(`/super-admin/anomaly-types/${id}`, data),
  deleteAnomalyType: (id: number): Promise<AxiosResponse<{ deleted: number }>> =>
    api.delete(`/super-admin/anomaly-types/${id}`),
};

// ── Estimates ─────────────────────────────────────────────────────────────────
export const estimatesApi = {
  // Pass an array to fetch several years in one round-trip (e.g. a 5-year chart)
  // instead of one request per year.
  list: (plantId: number, year: number | number[]): Promise<AxiosResponse<{ estimates: GenerationEstimate[] }>> =>
    Array.isArray(year)
      ? api.get('/estimates', { params: { plant_id: plantId, years: year.join(',') } })
      : api.get('/estimates', { params: { plant_id: plantId, year } }),

  upsert: (data: {
    plant_id?:     number;
    period_type:   string;
    year:          number;
    month?:        number | null;
    week_no?:      number | null;
    date?:         string | null;
    hour?:         number | null;
    estimated_kwh: number;
    notes?:        string;
  }): Promise<AxiosResponse<{ estimate: GenerationEstimate }>> =>
    api.post('/estimates', data),

  delete: (id: number): Promise<AxiosResponse<{ deleted: number }>> =>
    api.delete(`/estimates/${id}`),

  // Save many rows (e.g. a month + its weeks/days/hours) in a single request
  // instead of one POST per row.
  bulkUpsert: (estimates: Array<{
    plant_id?:     number;
    period_type:   string;
    year:          number;
    month?:        number | null;
    week_no?:      number | null;
    date?:         string | null;
    hour?:         number | null;
    estimated_kwh: number;
    notes?:        string;
  }>): Promise<AxiosResponse<{ ok: boolean; saved: number }>> =>
    api.post('/estimates/bulk', { estimates }),
};

// ── Shift Log ─────────────────────────────────────────────────────────────────
export interface ShiftLogEntry {
  id?:             number;
  log_date?:       string;
  hour:            number;
  equipment_type:  'generator' | 'transformer';
  equipment_id:    number;
  equipment_name?: string;
  // Generator fields
  output_kw?:      number | null;
  reactive_kvar?:  number | null;
  power_factor?:   number | null;
  voltage_ry_kv?:  number | null;
  voltage_yb_kv?:  number | null;
  voltage_br_kv?:  number | null;
  frequency_hz?:   number | null;
  current_i1_a?:   number | null;
  current_i2_a?:   number | null;
  current_i3_a?:   number | null;
  energy_kwh?:     number | null;
  // Transformer fields
  output_mw?:        number | null;
  active_power?:     number | null;
  voltage_kv?:       number | null;
  voltage_lv_kv?:    number | null;
  current_a?:        number | null;
  lv_voltage_ry_kv?: number | null;
  lv_voltage_yb_kv?: number | null;
  lv_voltage_br_kv?: number | null;
}

export const shiftLogApi = {
  get: (date: string): Promise<AxiosResponse<{ logs: ShiftLogEntry[]; hasData: boolean }>> =>
    api.get('/shift-log', { params: { date } }),

  getRange: (from: string, to: string): Promise<AxiosResponse<{ logs: (ShiftLogEntry & { log_date: string })[] }>> =>
    api.get('/shift-log/range', { params: { from, to } }),

  hourlyTotals: (params: { date: string }): Promise<AxiosResponse<{ data: Array<{ hour: number; combined_kw: string; avg_frequency: string | null; avg_power_factor: string | null; avg_voltage_ry: string | null }> }>> =>
    api.get('/shift-log/hourly-totals', { params }),

  dailyTotals: (params: { from: string; to: string }): Promise<AxiosResponse<{ data: import('../types').DailyGenerationSummary[] }>> =>
    api.get('/shift-log/daily-totals', { params }),

  bulkSave: (date: string, entries: ShiftLogEntry[]): Promise<AxiosResponse<{ ok: boolean; saved: number }>> =>
    api.post('/shift-log/bulk', { date, entries }),
};

export default api;
